import { clampDescription } from "../lib/serpFit";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { TOOLS } from "../lib/tools";
import { STATES } from "../lib/states";
import {
  SITE_NAME,
  FALLBACK_OG_IMAGE,
  absUrl,
} from "../lib/seo";
import {
  getSeoOverride,
  subscribeSeoOverrides,
} from "../lib/seoOverrides";

/**
 * Per-page SEO + Open Graph + Twitter + JSON-LD.
 * Usage: <SEO title="..." description="..." path="/guides" jsonLd={[...]} cluster="auto-loans" />
 *
 * Iter-127 — When an admin override row exists for `path` in the
 * `seo_overrides` collection, the override replaces the JSX defaults.
 * Falls back gracefully to the inline JSX values when the override is
 * empty or unset.
 *
 * Iter-162 — `cluster` prop accepts one of "auto-loans" | "refinance" |
 * "auto-insurance" | "warranty" and falls back to the matching branded
 * OG SVG in /public/og/ when no per-page `image` is set. Cluster-branded
 * social previews dramatically improve LinkedIn/Twitter/Discord CTR vs.
 * the generic FALLBACK_OG_IMAGE.
 */

// Iter-162 — Branded OG image map by editorial cluster.
// Iter-185.AL — Switched from .svg to .png. Facebook/Twitter/LinkedIn
// validators all prefer PNG for the og:image (some won't render SVG
// at all). Both formats are co-located in /og/ so admins can still
// edit the SVG source and re-rasterize via:
//   cd /app && python -m scripts.build_cluster_og_images
const CLUSTER_OG_IMAGES = {
  "auto-loans": "/og/auto-loans.png",
  refinance: "/og/refinance.png",
  "auto-insurance": "/og/auto-insurance.png",
  warranty: "/og/warranty.png",
  // Iter-275.Y — Cross-vertical compare page has its own card.
  compare: "/og/compare.png",
};

// Iter-422 — Global entity singletons.
// `index.html` ships the canonical Organization + WebSite entity graph
// (with `@id`s) as a static <script type="application/ld+json">. Dozens
// of pages ALSO pass `organizationLd()` into this component, so every
// page shipped the same entity two or three times (SSR injection +
// static graph + Helmet). Search engines discount conflicting duplicate
// entity markup, so we drop those two types here: the static graph in
// index.html is the single source of truth and is a strict superset.
// Anything page-scoped (Article, FAQPage, BreadcrumbList, Product, …)
// passes through untouched.
const GLOBAL_ENTITY_TYPES = new Set(["Organization", "WebSite"]);

// Iter-431 — per-page share cards (scripts/build_brand_kit.py): every
// calculator and every state insurance / auto-loan page has its own card.
const TOOL_SLUGS = new Set(TOOLS.map((t) => t.slug));
const STATE_SLUGS = new Set(STATES.map((s) => s.slug));
export function pageOgImage(path) {
  const p = (path || "").replace(/\/$/, "");
  const tool = p.slice(1);
  if (TOOL_SLUGS.has(tool)) return `/og/tools/${tool}.png`;
  const m = p.match(/^\/(insurance|auto-loans)\/([a-z-]+)$/);
  if (m && STATE_SLUGS.has(m[2])) return `/og/states/${m[1]}-${m[2]}.png`;
  return null;
}

function dedupeGlobalEntities(blocks) {
  if (!Array.isArray(blocks)) return [];
  return blocks.filter(
    (b) =>
      b &&
      typeof b === "object" &&
      !b["@graph"] &&
      !GLOBAL_ENTITY_TYPES.has(b["@type"]),
  );
}

export default function SEO({
  title,
  description,
  path = "/",
  image,
  cluster,
  type = "website",
  jsonLd = [],
  noindex = false,
  canonical: canonicalProp,
  brandSuffix = true,
}) {
  // Iter-127 — Subscribe so the page re-renders when the admin saves a
  // new override (the admin Preview pane uses this).
  const [, force] = useState(0);
  useEffect(() => subscribeSeoOverrides(() => force((n) => n + 1)), []);

  // Iter-416 (GSC) — Query-param variants (`?amount=…`, `?income=…`) are
  // handled by the self-referencing canonical to the clean path ONLY.
  // Iter-254 additionally emitted `noindex,follow` on them, but Google
  // documents noindex + cross-page canonical as conflicting signals, and
  // GSC shows 13 param variants earning impressions (one at position 4.9
  // with clicks) — consolidating them via canonical keeps that signal on
  // the clean URL instead of discarding it.
  const effectiveNoindex = noindex;

  const override = getSeoOverride(path) || {};
  const finalTitle = override.title || title;
  const finalDescription = clampDescription(override.description || description);
  // Resolution order (highest priority first):
  //   1. SEO override (admin Preview pane override row)
  //   2. Per-page `image` prop
  //   3. Cluster-branded OG fallback (iter-162)
  //   4. Site-wide FALLBACK_OG_IMAGE
  const clusterOg = cluster && CLUSTER_OG_IMAGES[cluster];
  const finalImage =
    override.og_image || image || pageOgImage(path) || clusterOg || FALLBACK_OG_IMAGE;
  // Iter-419 — Brand suffix is appended ONCE. A handful of editorial
  // titles already contain "CarSavr" (e.g. "The 5-Step CarSavr
  // Playbook"), which produced "… CarSavr Playbook | CarSavr". Bing's
  // report calls this duplicated branding.
  const alreadyBranded =
    !!finalTitle &&
    (finalTitle === SITE_NAME ||
      finalTitle.endsWith(`| ${SITE_NAME}`) ||
      finalTitle.endsWith(`- ${SITE_NAME}`) ||
      finalTitle.includes(SITE_NAME));
  const fullTitle = finalTitle
    ? alreadyBranded || (!brandSuffix && !override.title)
      ? finalTitle
      : `${finalTitle} | ${SITE_NAME}`
    : SITE_NAME;
  // Iter-193.C — Canonical resolution order:
  //   1. Admin SEO override
  //   2. Explicit `canonical` prop (used for persona-first canonicalization)
  //   3. Auto-derived from `path`
  // Iter-416 — always absolutized: 4 templates passed a relative
  // `canonical="/auto-loans/<state>"`, which Bing rendered verbatim as a
  // relative (and therefore conflicting) canonical.
  const canonical = absUrl(override.canonical || canonicalProp || path);
  const ogImage = absUrl(finalImage);

  // Iter-185.AI — Only emit og:image:width/height when we KNOW the
  // image is a 1200×630 brand asset (FALLBACK_OG_IMAGE or one of the
  // cluster OGs). For article hero images that come from Pexels at
  // arbitrary aspect ratios, omit the dims so FB/X validators
  // measure the actual image instead of trusting an incorrect hint.
  // Iter-185.AR — `/og/articles/<slug>.png` is also a known
  // 1200×630 brand asset (rendered by the backend factory).
  const isKnownBrandOg =
    finalImage === FALLBACK_OG_IMAGE ||
    finalImage === clusterOg ||
    /^\/og\/articles\/[^/]+\.png$/.test(finalImage || "");
  const imageAlt = `${finalTitle || SITE_NAME} — ${SITE_NAME}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {finalDescription && <meta name="description" content={finalDescription} />}
      <link rel="canonical" href={canonical} />
      {effectiveNoindex && (
        <meta
          name="robots"
          content={noindex ? "noindex,nofollow" : "noindex,follow"}
        />
      )}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={fullTitle} />
      {finalDescription && <meta property="og:description" content={finalDescription} />}
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:secure_url" content={ogImage} />
      {isKnownBrandOg && <meta property="og:image:width" content="1200" />}
      {isKnownBrandOg && <meta property="og:image:height" content="630" />}
      <meta property="og:image:alt" content={imageAlt} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@carsavr" />
      <meta name="twitter:creator" content="@carsavr" />
      <meta name="twitter:title" content={fullTitle} />
      {finalDescription && <meta name="twitter:description" content={finalDescription} />}
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={imageAlt} />

      {/* JSON-LD */}
      {dedupeGlobalEntities(jsonLd).map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}

      {/* Theme + base — keep aligned with index.html so mobile chrome doesn't flash */}
      <meta name="theme-color" content="#0A2540" />
      {/* Iter-199 (GSC fix) — hreflang alternate must SELF-reference the
          page's canonical URL. It previously pointed every page at the
          homepage, which told Google each page was an "alternate" of
          the root — a driver of bogus "Alternate page with proper
          canonical tag" exclusions in Search Console. */}
      <link rel="alternate" hrefLang="en" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />
    </Helmet>
  );
}
